import { LANGUAGE_EN } from '@home/_config/config';
import classNames from 'classnames';
import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'tea-component';
import { useIntl } from 'umi';
import AgentPointDown from '../../../svg/agent-point-down.svg';
import AgentPointDownActive from '../../../svg/agent-point-down-active.svg';
import AgentModeDefaultIcon from '../../../svg/agent-mode-default-icon.svg';
import BasicImage from '@home/components/BasicImage';
import { IAgentData, EAgentCategory } from '../../types';
import AgentListItem from './components/AgentListItem';
import AgentGroupList from './components/AgentGroupList';
import './index.scss';
import { useHover, useSize } from 'ahooks';
import { useDismissOutside } from '@/pages/aigc/pages/Agent/components/QAInput/hooks/useDismissOutside';

interface IAgentSelectorProps {
  /** Agent 列表 */
  list: IAgentData[];
  /** 外部控制的选中 Agent ID（可选） */
  selectedId?: string;
  /** 选择 Agent 的回调 */
  onSelect?: (agent: IAgentData) => void;
  /** 自定义类名 */
  className?: string;
  /** 自定义头部组件（可选） */
  customHeader?: ReactNode;
  /** 是否渲染图片（默认 true） */
  showIcon?: boolean;
  /** 自定义触发按钮（完全自定义按钮内容） */
  renderTrigger?: (currentAgent: IAgentData | undefined) => ReactNode;
  /** 自定义 Dropdown overlay 类名（可选） */
  overlayClassName?: string;
  /** 外部控制的打开状态 */
  isOpen?: boolean;
  /** 打开状态变化回调 */
  onOpenChange?: (isOpen: boolean) => void;
  /** 自定义下拉菜单样式（可选） */
  style?: React.CSSProperties;
  /** 是否按分类分组显示（默认 false，扁平列表） */
  grouped?: boolean;
  /** 下拉弹层定位（不传则使用组件默认自适应） */
  placement?: 'bottom-start' | 'top-start' | 'bottom-end' | 'top-end';
  /** 是否允许弹层自动翻转位置 */
  flipped?: boolean;
  /**
   * 是否允许浮层跟随参考元素溢出视口
   * 配合 `flipped={false}` 时，可阻止 popper preventOverflow 将浮层推回视口
   * 从而保证浮层始终锚定在按钮下方（极小屏下不会覆盖按钮）
   */
  escapeWithReference?: boolean;
  /** 是否允许 hover 打开下拉，默认 true；Copilot 传 false 仅 click 打开 */
  enableHoverOpen?: boolean;
}

/**
 * Agent 选择器组件
 */
const AgentSelector: FC<IAgentSelectorProps> = ({
  list,
  selectedId: externalSelectedId,
  onSelect,
  className = '',
  customHeader,
  showIcon,
  renderTrigger,
  overlayClassName,
  isOpen,
  onOpenChange,
  style,
  grouped = false,
  placement,
  flipped,
  escapeWithReference,
  enableHoverOpen = true,
}) => {
  const { locale, formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  
  // 内部状态管理选中的 Agent ID
  const [internalSelectedId, setInternalSelectedId] = useState<string>(
    externalSelectedId || ''
  );
  // hover 状态
  const [isHovered, setIsHovered] = useState(false);
  // 控制 Dropdown 打开/关闭状态（如果外部没有传入 isOpen，则使用内部状态）
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  // 使用外部状态或内部状态
  const isDropdownVisible = isOpen !== undefined ? isOpen : internalIsOpen;
  const setIsDropdownVisible = (value: boolean) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setInternalIsOpen(value);
    }
  };
  
  // Dropdown 容器的 ref 引用
  const dropdownRef = useRef<HTMLDivElement>(null);
  // button 的 ref 引用
  const buttonRef = useRef<HTMLDivElement>(null);
  
  // 监听外部 selectedId 变化，同步到内部状态
  useEffect(() => {
    setInternalSelectedId(externalSelectedId || list[0]?.agentId || '');
  }, [externalSelectedId]);
  
  // 标记是否是通过点击列表元素触发的关闭
  const isSelectClickedRef = useRef(false);
  // 延迟关闭的定时器
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 监听外部 isOpen 变化，当外部强制关闭时，清除延迟关闭定时器
  useEffect(() => {
    if (isOpen !== undefined) {
      // 如果外部强制关闭，清除定时器并重置标记
      if (!isOpen) {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        isSelectClickedRef.current = false;
      }
    }
  }, [isOpen]);
  
  // 使用 ref 监听 hover 状态
  const isHovering = useHover(dropdownRef);
  const isHeaderHovering = useHover(buttonRef);

  // 浮窗尺寸变化触发 re-render，配合 Dropdown 的 updateOnChildrenChange 自动重定位
  useSize(placement ? null : dropdownRef);
  
  // 面板外点击关闭（含 iframe / 宿主，公共 hook 统一处理）
  useDismissOutside({
    enabled: isDropdownVisible,
    onClose: () => {
      setIsDropdownVisible(false);
      isSelectClickedRef.current = false;
    },
    insideSelectors: [dropdownRef, buttonRef],
  });
  
  // 监听 hover 状态，自动关闭下拉菜单（click-only 模式由外点关闭）
  useEffect(() => {
    if (!enableHoverOpen) return;

    // 如果当前下拉菜单未打开，不需要处理关闭逻辑
    if (!isDropdownVisible) {
      return;
    }
    
    // 清除之前的定时器
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    // 如果鼠标在触发按钮或下拉菜单内，保持打开状态
    if (isHovering || isHeaderHovering) {
      return;
    }

    // 如果是通过点击列表元素触发的，不自动关闭
    if (isSelectClickedRef.current) {
      return;
    }

    // 延迟关闭，给用户时间从触发按钮移动到下拉菜单
    closeTimerRef.current = setTimeout(() => {
      setIsDropdownVisible(false);
    }, 200); // 200ms 延迟

    // 清理函数
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, [enableHoverOpen, isHeaderHovering, isHovering, isDropdownVisible]);
  const currentAgent = list.find(agent => agent.agentId === internalSelectedId) || list[0];

  // 判断是否显示图标：props 显式传入优先，否则取数据配置，默认 true
  const shouldShowIcon = showIcon ?? currentAgent.showIcon ?? true;
  // 获取触发按钮使用的图标：优先使用 activeIcon，否则使用 icon
  const triggerIconSrc = currentAgent?.activeIcon || currentAgent?.icon;
  // 获取下拉箭头图标：优先使用配置的 arrowIcon，根据 hover 状态切换
  const arrowIconSrc = isHovered 
    ? (currentAgent?.arrowIconActive || AgentPointDownActive)
    : (currentAgent?.arrowIcon || AgentPointDown);

  // 分组数据（仅 grouped=true 时使用）
  const groupedAgents = useMemo(() => {
    if (!grouped) return [];

    const groups: { categoryName: string; categoryNameEn: string; agents: IAgentData[]; }[] = [];
    const groupMap = new Map<string, { categoryName: string; categoryNameEn: string; agents: IAgentData[]; }>();

    list.forEach(agent => {
      // 简化分组：是 dataAgent 就归入数据Agent，其他统一归入通用对话
      const key = agent.category === EAgentCategory.DATA_AGENT
        ? EAgentCategory.DATA_AGENT
        : EAgentCategory.GENERAL_CHAT;

      if (groupMap.has(key)) {
        groupMap.get(key)!.agents.push(agent);
      } else {
        const group = {
          categoryName: key,
          categoryNameEn: key,
          agents: [agent],
        };
        groups.push(group);
        groupMap.set(key, group);
      }
    });

    return groups;
  }, [list, grouped]);

  // 处理 Agent 选择
  const handleSelect = (agent: IAgentData) => {
    // 标记是通过点击列表元素触发的
    isSelectClickedRef.current = true;
    // 更新内部状态
    setInternalSelectedId(agent.agentId);
    // 触发回调
    onSelect?.(agent);
    handleTriggerHover();
  };
  // 阻止触发器点击事件冒泡，防止关闭下拉菜单
  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleTriggerHover();
  };
  // 处理触发器 hover 事件，打开 Dropdown
  const handleTriggerHover = () => {
    setIsDropdownVisible(true);
  };

  // 渲染触发器按钮
  const triggerButtonInner = renderTrigger ? (
    renderTrigger(currentAgent)
  ) : (
    <div
      ref={buttonRef}
      className={classNames("dba-agent-selector__trigger", currentAgent?.className)}
      onMouseEnter={() => {
        setIsHovered(true);
        if (enableHoverOpen) {
          handleTriggerHover();
        }
      }}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleTriggerClick}
    >
      {shouldShowIcon && (
        <div className="dba-agent-selector__trigger-icon">
          <BasicImage src={triggerIconSrc} errorSrc={AgentModeDefaultIcon} />
        </div>
      )}
      <span className="dba-agent-selector__trigger-text">
        {isEN ? currentAgent?.name : currentAgent?.name_cn || formatMessage({ id: 'dba_collection_selector_default_name' })}
      </span>
      <img
        src={arrowIconSrc}
        alt=""
        className="dba-agent-selector__trigger-arrow"
      />
    </div>
  );

  const triggerButton = triggerButtonInner;

  const dropdown = (
    <Dropdown
      button={triggerButton}
      className={`dba-agent-selector ${className}`}
      overlayClassName={classNames("dba-agent-selector-dropdown-overlay", "dba-nav-menu-overlay", overlayClassName)}
      trigger={enableHoverOpen ? 'hover' : 'click'}
      appearance='pure'
      placement={placement}
      open={isDropdownVisible}
      updateOnChildrenChange={true}
      flipped={flipped}
      escapeWithReference={escapeWithReference}
    >
      {(close) => (
        <div 
          className="dba-agent-selector__dropdown" 
          ref={dropdownRef}
          style={style}
        >
          {customHeader}
          <div className="dba-agent-selector__list">
            {grouped ? (
              /* 分组模式：按分类展示，带 Collapse 展开/收起动画 */
              <AgentGroupList
                groups={groupedAgents}
                selectedId={internalSelectedId}
                isEN={isEN}
                onSelect={(agent) => {
                  handleSelect(agent);
                  close();
                }}
                formatMessage={formatMessage}
              />
            ) : (
              /* 扁平模式：直接列出所有 Agent */
              list.map((agent) => (
                <AgentListItem
                  key={agent.agentId}
                  agent={agent}
                  isSelected={agent.agentId === internalSelectedId}
                  isEN={isEN}
                  onClick={(a) => {
                    handleSelect(a);
                    close();
                  }}
                  formatMessage={formatMessage}
                />
              ))
            )}
          </div>
        </div>
      )}
    </Dropdown>
  );

  if (!enableHoverOpen) {
    return (
      <div onClick={(e) => e.stopPropagation()}>{dropdown}</div>
    );
  }

  return dropdown;
};

export default AgentSelector;


